import { Alert, Button } from 'antd'
import { useTranslation } from 'react-i18next'
import { Icon, StatCard } from '@mysaiva/core'
import { useApiKeyUsage } from '../../api-keys/useApiKeys'
import { isUsageEmpty } from '../../usage/usage.types'
import {
  formatCount,
  formatMs,
  formatPercent,
  msChange,
  percentChange,
  pointChange
} from '../../usage/format'
import styles from './ApiKeys.module.scss'

/**
 * Per-key usage (last 30 days) with the same states as the dashboard usage
 * row: skeleton, inline error with retry, zero-data placeholders, and an
 * "unavailable" note while the backend does not serve per-key usage.
 */
export function KeyUsageTab({ keyId }: { keyId: string }) {
  const { t } = useTranslation()
  const { data, isPending, isError, refetch } = useApiKeyUsage(keyId)
  const tr = (key: string, options?: Record<string, unknown>) =>
    t(`developerPlatform.dashboard.usage.${key}`, options)

  if (isError) {
    return (
      <Alert
        type="error"
        showIcon
        message={tr('errorTitle')}
        description={tr('errorText')}
        action={
          <Button size="small" onClick={() => refetch()} data-testid="key-usage-retry">
            {tr('retry')}
          </Button>
        }
        data-testid="key-usage-error"
      />
    )
  }

  if (!isPending && data === null) {
    return (
      <p className={styles.muted} data-testid="key-usage-unavailable">
        {t('developerPlatform.apiKeys.usage.unavailable')}
      </p>
    )
  }

  const empty = !isPending && data !== undefined && data !== null && isUsageEmpty(data)
  const summary = data ?? null
  const calls = summary
    ? percentChange(summary.apiCalls.total, summary.apiCalls.previousTotal)
    : null
  const latency = summary ? msChange(summary.latency.avgMs, summary.latency.previousAvgMs) : null
  const success = summary
    ? pointChange(summary.successRate.percent, summary.successRate.previousPercent)
    : null
  const days = summary?.periodDays ?? 30

  return (
    <div className={styles.usageGrid} aria-busy={isPending} data-testid="key-usage">
      <StatCard
        loading={isPending}
        empty={empty}
        value={summary ? formatCount(summary.apiCalls.total) : ''}
        label={tr('apiCalls')}
        emptyHint={tr('emptyHint')}
        icon={<Icon icon="Chart" size={18} />}
        trend={
          calls
            ? {
                direction: calls.direction,
                tone: calls.raw >= 0 ? 'positive' : 'negative',
                value: calls.value,
                caption: tr('vsPrior', { days })
              }
            : undefined
        }
        data-testid="key-usage-apiCalls"
      />
      <StatCard
        loading={isPending}
        empty={empty}
        value={summary?.latency.avgMs != null ? formatMs(summary.latency.avgMs) : '—'}
        unit={summary?.latency.avgMs != null ? 'ms' : undefined}
        label={tr('latency')}
        emptyHint={tr('emptyHint')}
        icon={<Icon icon="Clock" size={18} />}
        iconTone="pale-blue"
        trend={
          latency
            ? {
                direction: latency.raw < 0 ? 'up' : 'down',
                tone: latency.raw < 0 ? 'positive' : 'negative',
                value: latency.value,
                caption: tr(latency.raw < 0 ? 'faster' : 'slower', { days })
              }
            : undefined
        }
        data-testid="key-usage-latency"
      />
      <StatCard
        loading={isPending}
        empty={empty}
        value={
          summary?.successRate.percent != null ? formatPercent(summary.successRate.percent) : '—'
        }
        unit={summary?.successRate.percent != null ? '%' : undefined}
        label={tr('successRate')}
        emptyHint={tr('emptyHint')}
        icon={<Icon icon="CheckCircle" size={18} />}
        trend={
          summary
            ? {
                direction: success?.direction,
                tone: success ? (success.raw >= 0 ? 'positive' : 'negative') : 'neutral',
                value: success?.value ?? '',
                caption: tr('errors', { count: summary.successRate.errorCount })
              }
            : undefined
        }
        data-testid="key-usage-successRate"
      />
      <StatCard
        loading={isPending}
        empty={empty}
        value={summary ? formatCount(summary.successRate.errorCount) : ''}
        label={t('developerPlatform.apiKeys.usage.errors')}
        emptyHint={tr('emptyHint')}
        icon={<Icon icon="WarningCircle" size={18} />}
        iconTone="pale-blue"
        data-testid="key-usage-errorCount"
      />
    </div>
  )
}
